import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { SelectAsyncControlled } from '#components/patterns/forms/select/select-async/SelectAsyncControlled.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { FirewallRuleFieldValue } from '../types.ts';
import { rolesForSelectionQueryOptions } from './FirewallRuleForm.queries.ts';
import { PortsField } from './ports-field/PortsField.tsx';

const PROTOCOL_LABELS = { TCP: 'TCP', UDP: 'UDP', ICMP: 'ICMP', ANY: 'Any' };
const PROTOCOLS = ['ANY', 'TCP', 'UDP', 'ICMP'] as const;

type FormData = FirewallRuleFieldValue;

type Props = {
  rule: FirewallRuleFieldValue;
  onSave: (values: FirewallRuleFieldValue) => void;
};

export function FirewallRuleForm({ rule, onSave }: Props) {
  const defaultValues = rule;
  const formContext = useForm<FormData>({ defaultValues });
  const { register, handleSubmit, formState, control } = formContext;
  const { errors } = formState;

  const watchedProtoValue = useWatch({ control, name: 'protocol' });

  const onSubmit = handleSubmit((data) => {
    return Promise.resolve(onSave(data));
  });

  return (
    <FormProvider {...formContext}>
      <form method="post" id="firewall-rule-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="wide">
          <Field label="Description" className="width-100">
            <Input
              className="width-100"
              maxLength={255}
              hasError={Boolean(errors.description)}
              {...register('description')}
            />
            {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
          </Field>
          <Spacer size="wide" stackWhenXNarrow={true}>
            <SelectControlled
              className="width-100"
              control={control}
              name="protocol"
              label="Protocol"
              placeholder="Select a protocol…"
              errorMessage={errors.protocol?.message}
              renderValue={(item: keyof typeof PROTOCOL_LABELS) => PROTOCOL_LABELS[item]}
              renderOptions={({ onChange }) => (
                <>
                  {PROTOCOLS.map((proto) => (
                    <SelectItem key={proto} value={proto} onClick={() => onChange(proto)}>
                      {PROTOCOL_LABELS[proto]}
                    </SelectItem>
                  ))}
                </>
              )}
            />
            <PortsField className="width-100" disabled={watchedProtoValue === 'ICMP'} />
          </Spacer>
          <div>
            <h3 className="h5 mt-0 mb-16">From hosts matching</h3>
            <SelectAsyncControlled
              className="width-100 mb-16"
              getItemsQueryOptions={rolesForSelectionQueryOptions}
              control={control}
              name="allowedRoleID"
              label="Role"
              placeholder="Select a role…"
              errorMessage={errors.allowedRoleID?.message}
              renderOptions={({ onChange, items }) => (
                <>
                  {items.map((itm) => (
                    <SelectItem key={itm.value} value={itm.value} onClick={() => onChange(itm.value)}>
                      {itm.label}
                    </SelectItem>
                  ))}
                </>
              )}
            />
            <Divider className="mb-12">AND</Divider>
            <ComboboxTags name="tags" label="Tags" control={control} unknownTags="reference" />
          </div>
        </Spacer>
      </form>
    </FormProvider>
  );
}
